<template>
    <div class="container">
        <h1>Qu'est-ce que le Staking ?</h1>

        <p class="intro">
            "Gagnez 4% à 8% d'intérêt par an sans rien faire", le staking ressemble parfois à une mauvaise publicité vous demandant
            de faire confiance à une entreprise douteuse. Pourtant, ce procédé mécompris est bien au coeur des blockchain
            <a href="qu-est-ce-que-le-delegated-proof-of-stake-dpos">PoS et DPoS</a> est nécessaire: il permet de valider les transactions de la
            blockchain, et récompenses les participants pour cela. Explications.
        </p>

        <h2>Une histoire de transactions</h2>
        <p>
            Comme expliqué dans notre documentation sur le <a href="qu-est-ce-que-le-delegated-proof-of-stake-dpos">Delegated Proof of Stake</a>,
            Le <b>staking</b> est au blockchains PoS (comme PolkaDot, Lisk, et bientot Ethereum) et DPoS (comme Cardano) ce que le <b>minage</b>
            est au blockchain <b>Proof of Work</b> (bitcoin): un système de validation de transaction. Chaque blockchain dispose de son propre algorithme de
            consensus pour déterminer qui aura le droit d'écrire le prochain bloc, et celui du PoS (et DPoS) se base sur une lotterie, ou chacun
            possède autant de tickets que la somme d'unité de cryptomonnaie qu'il a accumulé.
        </p>

        <p>
            <u>Le staking consiste donc à <i>brancher son portefeuille</i> sur l'algorithme de consensus de la blockchain</u>, pour tenter de gagner le
            droit d'écrire le prochain bloc (et gagner les récompenses qui vont avec). Ces récompenses sont en général issues des
            <b>frais de transaction</b> payés
            par les utilisateurs de la blockchain qui font des virements de cryptomonnaie, parfois complétés (dégressivements) par <b>une partie de la
                réserve</b> de cryptomonnaie.
        </p>

        <h2>Comment faire du staking</h2>

        <p>Selon les types de blockchain, le processus pour s'engager dans du staking change drastiquement</p>

        <h3>Le Staking PoS: Devenez Noeud ou confiez vos fonds à un Noeud.</h3>
        <p>
            Si vous voulez faire du staking pour des blockchains PoS, vous avez <b>la réponsabilité de faire tourner un noeud du réseau</b>, ou <b>
                d'envoyer vos fonds à quelqu'un qui le fait pour vous</b>, ce qui n'est pas une perspective très alléchante. N'oublions pas la citation
            célèbre de <i>Andreas Antonopoulos</i>, fameux entrepreneur du monde des cryptomonnaies:
        </p>

        <pre style="margin: 20px;"><code>"Your keys, your bitcoins. Not your keys, not your bitcoins."</code></pre>

        <p>
            Même si certains gros exchanges proposeront de faire du staking pour vous (comme Binance, ou Crypto.com par exemple), n'oubliez pas que
            chaque once de cryptomonnaie que vous leur confiez ne vous appartient pas, du point de vue des blockchains respectives.
            Elles appartiennent à l'exchange en question, qui vous offre la <i>guarantie</i> qu'elle vous les rendra quand vous le demanderez.
            <b>Mais que vaut vraiment cette garantie ?</b> Que se passe-t'il en cas de piratage, à quel point ces exchanges sont-ils sécurisé,
            jusqu'où sont-ils assurés ? A vous de décider. Sachez néanmoins que les
            <a href="https://fr.cryptonews.com/exclusives/c-est-quoi-une-baleine-2879.htm">baleines</a> ne laissent pas trainer leurs pactoles
            sur les exchanges.
        </p>

        <h3>Le Staking DPoS: L'Argent ne quitte pas votre portefeuille</h3>
        <p>
            C'est ici que se révèle le grand avantage du DPoS: C'est un système <b>trustless</b>. Vous n'avez besoin de faire confiance à personne.
            Sur Cardano, le staking est divisé entre 2 acteurs: les <a href="/doc-cardano/qu-est-ce-qu-une-stake-pool">Stake Pools</a> (ou groupes d'enjeus),
            qui opèrent des noeuds du réseau, et les délégateurs, détenteurs de portefeuilles, qui délègent le pouvoir de staking aux stake pools, en
            échange d'intérêts.
        </p>

        <p>
            Les Stake Pools collectent donc les enjeux des délégateurs afin d'augmenter leurs chances de pouvoir créer des blocs. Par chaque bloc créé,
            la stake pool à l'origine du bloc et ses délégateurs seront récompensés, à la fin de <b>l'époque</b>, par des récompenses.
            <b>Personne n'a besoin de prendre des risques</b> dans ce processus, mais tout le monde peut participer et être récompensé.
        </p>

        <h3>Comment puis-je faire du staking avec mes ADAs</h3>
        <p>
            Il vous faut recupérer vos ADAs sur un <a href="/doc-cardano/les-portefeuilles-compatibles-avec-le-staking-d-ADA">portefeuille compatible avec
                le staking Cardano</a>, puis suivre les instructions de celui-ci pour déléguer vos ADAs. Il est possible de le faire depuis des hardware
            wallets (portefeuilles dit "COLD"), comme les <b>Ledger</b> ou les <b>Trezor</b>.
        </p>

        <p>Suivez nos tutoriels:</p>
        <ul>
            <li><a href="/tutoriel/comment-staker-ses-ADAs-avec-Yoroi">Comment faire du Staking avec mes ADAs depuis Yoroi (HOT/COLD wallet)</a></li>
            <li><a href="/tutoriel/comment-staker-ses-ADAs-avec-Adalite">Comment faire du Staking avec mes ADAs depuis Adalite (HOT/COLD wallet)</a></li>
            <li><a href="/tutoriel/comment-staker-ses-ADAs-avec-Daedalus">Comment faire du Staking avec mes ADAs depuis Daedalus (HOT wallet)</a></li>
        </ul>
    </div>
</template>

<script>
import createHeader from "../../js/meta.js";

export default {
    layout: "DocLayout",
    head: createHeader({
        title: "Qu'est-ce que le Staking - Stakepool.fr",
        description:
            "Le Staking permet à ses utilisateur de gagner des intérêts sur leur crypto-monnaies sans qu'elles quittent leur portefeuille, en participant aux transaction de la blockchain. Explications"
    })
};
</script>